import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Button, Container, Paper, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useCookies } from 'react-cookie';
import './ContactUs.css';
import Footer from './Footer';
import LawAppBar from './LawAppBar';

export default function ContactUs() {
    const [cookies] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
    const [submitMessage, setSubmitMessage] = useState('');

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.subject.trim()) {
            newErrors.subject = 'Subject is required';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        // Clear submit status when user makes changes
        if (submitStatus) {
            setSubmitStatus(null);
            setSubmitMessage('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);
        setSubmitMessage('');

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (response.ok) {
                setSubmitStatus('success');
                setSubmitMessage('Thank you for contacting us! We\'ll get back to you soon.');
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
                setSubmitMessage(data.message || 'Failed to send message. Please try again later.');
            }
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setSubmitStatus('error');
            setSubmitMessage('An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Container
                maxWidth="md"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px'
                }}
            >
                <Box sx={{ textAlign: 'center', marginBottom: '40px' }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.4rem' },
                            marginBottom: '0.8rem',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Contact Us
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: '1.1rem',
                            fontWeight: 400,
                            lineHeight: 1.5,
                            maxWidth: 600,
                            margin: '0 auto'
                        }}
                    >
                        Have a question or feedback? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
                    </Typography>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '24px', sm: '40px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}
                >
                    {submitStatus && (
                        <Alert
                            severity={submitStatus}
                            sx={{
                                marginBottom: '24px',
                                borderRadius: '8px'
                            }}
                            onClose={() => {
                                setSubmitStatus(null);
                                setSubmitMessage('');
                            }}
                        >
                            {submitMessage}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            margin="normal"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            margin="normal"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            error={!!errors.subject}
                            helperText={errors.subject}
                            margin="normal"
                            required
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            error={!!errors.message}
                            helperText={errors.message}
                            margin="normal"
                            required
                            multiline
                            rows={6}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--accent-color)',
                                    },
                                },
                            }}
                        />

                        <Box sx={{ marginTop: '32px', textAlign: 'center' }}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={isSubmitting}
                                startIcon={<SendIcon />}
                                sx={{
                                    background: 'var(--button-gradient)',
                                    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '1.05rem',
                                    padding: '15px 32px',
                                    borderRadius: '16px',
                                    minHeight: '56px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
                                        background: 'var(--button-gradient-hover)'
                                    },
                                    '&:disabled': {
                                        background: 'var(--text-disabled)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Container>
            <Footer />
        </div>
    );
}
